/**
 * Pure tree logic for AuiFiles — extracted so it can be unit-tested without
 * mounting the component. Two sourcing modes feed the SAME flat-row renderer:
 *
 *  - EAGER (VFS): the whole file list is in memory. `buildTree` synthesises
 *    folders from file paths, `flattenTree` walks it in alphabetical order.
 *
 *  - LAZY (KB): children load per-folder on demand into a `FolderStore`.
 *    `buildLazyTree` materialises only the loaded folders, preserving the
 *    backend's server order (never re-sorted — a paginated folder can't be
 *    sorted client-side). `flattenTree` emits spinner / empty / error /
 *    load-more sentinel rows from each folder's load state.
 *
 * Both produce `FlatRow[]`, which the virtualizer windows and renders one row
 * at a time. See AuiFiles.tsx.
 */

import type { FilesEntry, FilesEvent, FolderChild } from './files-backend';
// Import the comparator directly from the source module (not the barrel) so
// this pure module doesn't pull the KB/VFS adapters — keeps it unit-testable
// without stubbing `@/services/api` / platform-events.
import { compareChildren } from './files-backend/order';

export interface TreeNode {
  name: string;
  /** Full path from the owner root. */
  path: string;
  /** Present only on file nodes. */
  entry?: FilesEntry;
  children: Map<string, TreeNode>;
}

export function basename(path: string): string {
  return path.split('/').pop() || path;
}

// ─── Eager tree (VFS) ─────────────────────────────────────────────────────

/**
 * Build a folder tree from a flat file list, synthesising intermediate
 * folders from each path. `extraFolderPaths` layers in folders the backend
 * reports that no file reaches (persisted empty folders / virtual folders).
 */
export function buildTree(
  entries: FilesEntry[],
  rootPrefix: string,
  extraFolderPaths: string[] = [],
): TreeNode {
  const root: TreeNode = { name: '', path: rootPrefix, children: new Map() };
  for (const entry of entries) {
    const rel = rootPrefix ? entry.path.slice(rootPrefix.length + 1) : entry.path;
    const segments = rel.split('/').filter(Boolean);
    if (segments.length === 0) continue;
    let cursor = root;
    let cumulative = rootPrefix;
    for (let i = 0; i < segments.length; i++) {
      const seg = segments[i];
      cumulative = cumulative ? `${cumulative}/${seg}` : seg;
      let child = cursor.children.get(seg);
      if (!child) {
        child = { name: seg, path: cumulative, children: new Map() };
        cursor.children.set(seg, child);
      }
      if (i === segments.length - 1) child.entry = entry;
      cursor = child;
    }
  }
  for (const folderPath of extraFolderPaths) {
    const rel = rootPrefix ? folderPath.slice(rootPrefix.length + 1) : folderPath;
    const segments = rel.split('/').filter(Boolean);
    if (segments.length === 0) continue;
    let cursor = root;
    let cumulative = rootPrefix;
    for (const seg of segments) {
      cumulative = cumulative ? `${cumulative}/${seg}` : seg;
      let child = cursor.children.get(seg);
      if (!child) {
        child = { name: seg, path: cumulative, children: new Map() };
        cursor.children.set(seg, child);
      }
      cursor = child;
    }
  }
  return root;
}

/** Alphabetical child order: folders first, then files, name-asc. Eager only. */
export function sortChildren(node: TreeNode): TreeNode[] {
  const arr = Array.from(node.children.values());
  arr.sort((a, b) => {
    const aFolder = !a.entry;
    const bFolder = !b.entry;
    if (aFolder !== bFolder) return aFolder ? -1 : 1;
    return a.name.localeCompare(b.name);
  });
  return arr;
}

// ─── Lazy store (KB) ──────────────────────────────────────────────────────

export type FolderStatus =
  | 'idle'
  | 'loading'
  | 'loadingMore'
  | 'loaded'
  | 'error';

export interface FolderState {
  status: FolderStatus;
  /** Accumulated children across pages, in the backend's server order. */
  children: FolderChild[];
  cursor: string | null;
  hasMore: boolean;
  error?: string;
  totalCount?: number;
}

/** Per-folder load state, keyed by folder path ('' = root). */
export type FolderStore = Map<string, FolderState>;

/**
 * Materialise the loaded folders into a tree, preserving each folder's
 * server order (children are NOT sorted — a partially-paginated folder can't
 * be). Folder nodes are created both from their parent's folder-child rows
 * and from their own store entry, so an expanded-but-empty folder still
 * appears.
 */
export function buildLazyTree(store: FolderStore, rootPrefix: string): TreeNode {
  const root: TreeNode = {
    name: basename(rootPrefix),
    path: rootPrefix,
    children: new Map(),
  };

  // Walk from root creating any missing intermediate nodes for `path`.
  const ensureNode = (path: string): TreeNode | null => {
    if (path === rootPrefix || path === '') return root;
    const rel = rootPrefix ? path.slice(rootPrefix.length + 1) : path;
    const segments = rel.split('/').filter(Boolean);
    let cursor = root;
    let cumulative = rootPrefix;
    for (const seg of segments) {
      cumulative = cumulative ? `${cumulative}/${seg}` : seg;
      let child = cursor.children.get(seg);
      if (!child) {
        child = { name: seg, path: cumulative, children: new Map() };
        cursor.children.set(seg, child);
      }
      cursor = child;
    }
    return cursor;
  };

  for (const [folderPath, state] of store) {
    const parent = ensureNode(folderPath);
    if (!parent) continue;
    for (const child of state.children) {
      const seg = child.name;
      let node = parent.children.get(seg);
      if (!node) {
        node = { name: seg, path: child.path, children: new Map() };
        parent.children.set(seg, node);
      }
      if (child.kind === 'file' && child.entry) node.entry = child.entry;
    }
  }
  return root;
}

// ─── Flatten to rows (shared renderer input) ──────────────────────────────

export type FlatRowVariant =
  | 'folder'
  | 'file'
  | 'spinner'
  | 'empty'
  | 'error'
  | 'load-more';

export interface FlatRow {
  variant: FlatRowVariant;
  /** Folder/file: the node path. Sentinels: the owning folder's path. */
  path: string;
  depth: number;
  // folder / file rows
  node?: TreeNode;
  isFolder?: boolean;
  open?: boolean;
  /** Folder can be expanded (always true for lazy folders; eager needs kids). */
  canExpand?: boolean;
  entry?: FilesEntry;
  // error rows
  message?: string;
  /** load-more: the cursor is implicit in the folder state; flag whether a
   *  follow-up page is already loading so the row can show a spinner. */
  loadingMore?: boolean;
}

export interface FlattenOptions {
  isFolderOpen: (path: string, depth: number) => boolean;
  lazy: boolean;
  /** Lazy only — the load state for a folder path. */
  folderState?: (path: string) => FolderState | undefined;
}

/**
 * Depth-first flatten in DOM order. Recurses into a folder only when it's
 * open. For lazy folders, emits sentinel rows (spinner / empty / error /
 * load-more) from the folder's load state.
 */
export function flattenTree(topNodes: TreeNode[], opts: FlattenOptions): FlatRow[] {
  const { isFolderOpen, lazy, folderState } = opts;
  const rows: FlatRow[] = [];

  const childrenOf = (node: TreeNode): TreeNode[] =>
    lazy ? Array.from(node.children.values()) : sortChildren(node);

  const walk = (nodes: TreeNode[], depth: number) => {
    for (const node of nodes) {
      const isFolder = !node.entry;
      if (!isFolder) {
        rows.push({
          variant: 'file',
          path: node.path,
          depth,
          node,
          isFolder: false,
          entry: node.entry,
        });
        continue;
      }

      const loadedChildren = node.children.size > 0;
      // Lazy folders are always expandable; eager folders need real children.
      const canExpand = lazy ? true : loadedChildren;
      const open = canExpand ? isFolderOpen(node.path, depth) : false;
      rows.push({
        variant: 'folder',
        path: node.path,
        depth,
        node,
        isFolder: true,
        open,
        canExpand,
      });
      if (!open) continue;

      if (!lazy) {
        if (loadedChildren) walk(childrenOf(node), depth + 1);
        continue;
      }

      // Lazy: consult the folder's load state for sentinel rows.
      const state = folderState?.(node.path);
      const status = state?.status;
      const childDepth = depth + 1;

      if (!loadedChildren && (status === undefined || status === 'idle' || status === 'loading')) {
        rows.push({ variant: 'spinner', path: node.path, depth: childDepth });
        continue;
      }
      if (!loadedChildren && status === 'error') {
        rows.push({
          variant: 'error',
          path: node.path,
          depth: childDepth,
          message: state?.error,
        });
        continue;
      }
      if (loadedChildren) {
        walk(childrenOf(node), childDepth);
      } else if (status === 'loaded') {
        rows.push({ variant: 'empty', path: node.path, depth: childDepth });
      }
      if (state?.hasMore) {
        rows.push({
          variant: 'load-more',
          path: node.path,
          depth: childDepth,
          loadingMore: status === 'loadingMore',
        });
      }
    }
  };

  walk(topNodes, 0);
  return rows;
}

/** Rows the keyboard navigation treats as focusable (sentinels are skipped). */
export function isNavigableRow(row: FlatRow): boolean {
  return row.variant === 'folder' || row.variant === 'file';
}

// ─── Live WS reconciliation into the lazy store ───────────────────────────

export function parentDir(path: string): string {
  const idx = path.lastIndexOf('/');
  return idx >= 0 ? path.slice(0, idx) : '';
}

/** A FolderChild for a file, built from an event-carried entry. */
function fileChild(entry: FilesEntry): FolderChild {
  return { kind: 'file', path: entry.path, name: basename(entry.path), entry };
}

/**
 * Insert a child into a loaded folder, preserving the backend's order.
 *
 * The subtle case: a partially-paginated folder (`hasMore`) only holds a
 * prefix of its children. A new child that sorts AFTER the last loaded one
 * belongs to an unloaded page — inserting it would show it out of order (it'd
 * appear before children we haven't fetched). So we drop it; it'll arrive when
 * the user loads more. Children that sort within the loaded prefix splice in
 * at their ordered position.
 */
function insertChild(state: FolderState, child: FolderChild): FolderState {
  const children = state.children;
  let idx = children.length;
  for (let i = 0; i < children.length; i++) {
    if (compareChildren(child, children[i]) < 0) {
      idx = i;
      break;
    }
  }
  if (idx === children.length && state.hasMore) return state; // beyond loaded prefix
  const next = children.slice();
  next.splice(idx, 0, child);
  return { ...state, children: next };
}

/** Replace or insert a file child (deduped by fileId) in its parent folder. */
function upsertFileChild(store: FolderStore, entry: FilesEntry): FolderStore {
  const dir = parentDir(entry.path);
  const state = store.get(dir);
  if (!state) return store; // parent not loaded — ignore (loads fresh on expand)
  const child = fileChild(entry);
  const existingIdx = state.children.findIndex(
    (c) => c.kind === 'file' && c.entry?.fileId === entry.fileId,
  );
  let nextState: FolderState;
  if (existingIdx >= 0) {
    const nextChildren = state.children.slice();
    nextChildren[existingIdx] = child;
    nextState = { ...state, children: nextChildren };
  } else {
    nextState = insertChild(state, child);
    if (nextState === state) return store; // dropped (beyond prefix)
  }
  const out = new Map(store);
  out.set(dir, nextState);
  return out;
}

/** Remove a file child by fileId (and/or path) from its parent folder. */
function removeFileChild(
  store: FolderStore,
  fileId?: string,
  path?: string,
): FolderStore {
  const dir = path ? parentDir(path) : undefined;
  let mutated = false;
  const out = new Map(store);
  const consider = (key: string, state: FolderState) => {
    const nextChildren = state.children.filter(
      (c) =>
        !(
          c.kind === 'file' &&
          ((fileId && c.entry?.fileId === fileId) || (path && c.path === path))
        ),
    );
    if (nextChildren.length !== state.children.length) {
      out.set(key, { ...state, children: nextChildren });
      mutated = true;
    }
  };
  if (dir !== undefined && store.has(dir)) {
    consider(dir, store.get(dir)!);
  } else {
    // No path hint (delete-by-fileId) — scan all loaded folders.
    for (const [key, state] of store) consider(key, state);
  }
  return mutated ? out : store;
}

/** Drop every store entry at or beneath `prefix` (folder move/delete). */
function dropSubtree(store: FolderStore, prefix: string): FolderStore {
  const sub = `${prefix}/`;
  let mutated = false;
  const out = new Map(store);
  for (const key of store.keys()) {
    if (key === prefix || key.startsWith(sub)) {
      out.delete(key);
      mutated = true;
    }
  }
  // Also remove the folder's own row from its parent's children.
  const dir = parentDir(prefix);
  const parent = store.get(dir);
  if (parent) {
    const nextChildren = parent.children.filter((c) => c.path !== prefix);
    if (nextChildren.length !== parent.children.length) {
      out.set(dir, { ...parent, children: nextChildren });
      mutated = true;
    }
  }
  return mutated ? out : store;
}

/** Patch a file child's index status in place if its parent is loaded. */
function patchIndexed(store: FolderStore, fileId: string, path?: string): FolderStore {
  const dir = path ? parentDir(path) : undefined;
  let mutated = false;
  const out = new Map(store);
  const consider = (key: string, state: FolderState) => {
    const idx = state.children.findIndex(
      (c) => c.kind === 'file' && c.entry?.fileId === fileId,
    );
    if (idx < 0) return;
    const child = state.children[idx];
    if (!child.entry || child.entry.indexStatus === 'indexed') return;
    const nextChildren = state.children.slice();
    nextChildren[idx] = { ...child, entry: { ...child.entry, indexStatus: 'indexed' } };
    out.set(key, { ...state, children: nextChildren });
    mutated = true;
  };
  if (dir !== undefined && store.has(dir)) consider(dir, store.get(dir)!);
  else for (const [key, state] of store) consider(key, state);
  return mutated ? out : store;
}

/**
 * Apply one normalised file event to the lazy store. Pure — returns the SAME
 * store reference when nothing changes (so React skips the re-render). Rule:
 * patch the parent folder if it's loaded; ignore otherwise (an unloaded folder
 * fetches fresh on expand). Returns `{ store, prune }` where `prune` is a path
 * whose virtual placeholder folder should be dropped (a file landed in it).
 */
export function reduceFolderStore(
  store: FolderStore,
  event: FilesEvent,
): { store: FolderStore; prune?: string } {
  switch (event.type) {
    case 'file-created':
    case 'file-updated':
      return { store: upsertFileChild(store, event.entry), prune: event.entry.path };
    case 'file-moved': {
      const removed = removeFileChild(store, event.fileId, event.from);
      const added = upsertFileChild(removed, event.entry);
      return { store: added, prune: event.entry.path };
    }
    case 'file-deleted':
      return { store: removeFileChild(store, event.fileId, event.path) };
    case 'file-indexed':
      return { store: patchIndexed(store, event.fileId, event.path) };
    case 'folder-created': {
      const dir = parentDir(event.path);
      const parent = store.get(dir);
      if (!parent) return { store };
      if (parent.children.some((c) => c.path === event.path)) return { store };
      const child: FolderChild = {
        kind: 'folder',
        path: event.path,
        name: basename(event.path),
      };
      const nextState = insertChild(parent, child);
      if (nextState === parent) return { store };
      const out = new Map(store);
      out.set(dir, nextState);
      return { store: out };
    }
    case 'folder-deleted':
      return { store: dropSubtree(store, event.path) };
    case 'folder-moved':
      // Drop the old subtree; the new location loads fresh on expand.
      return { store: dropSubtree(store, event.from) };
    default:
      return { store };
  }
}
